import { DownloadOutlined } from "@ant-design/icons";
import axios from "axios";
import { useCallback, useEffect, useRef, useState } from "react";

import SessionStore from "../model/SessionStore";
import type {
  MetricResponse,
} from "../model/model";

import { UPlotOptsBuilder } from "../uplot/UPlotOptsBuilder";
import { UPlotReact } from "../uplot/UPlotReact";
import { AlignedData } from "uplot";
import 'uplot/dist/uPlot.min.css';

interface IProps {
  metric: MetricResponse;
  aggregation: string;
  zeroToNull?: boolean;
  width?: number;
  height?: number;
  title?: string;
  deviceId?: string;
  startDate?: Date;
  endDate?: Date;
  limit?: number;
}

function MetricChart(props: IProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState<number>(props.width || 600);

  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerWidth(Math.floor(entry.contentRect.width));
      }
    });
    observer.observe(containerRef.current);
    setContainerWidth(Math.floor(containerRef.current.getBoundingClientRect().width));
    return () => observer.disconnect();
  }, []);

  const downloadCsv = useCallback(async () => {
    if (!props.deviceId || !props.startDate || !props.endDate) {
      return;
    }
    const token = SessionStore.getToken();
    const response = await axios.post("/api/v1/metrics/csv", {
      ds: props.deviceId,
      from: props.startDate.getTime(),
      to: props.endDate.getTime(),
      lim: props.limit || 10000,
      lastRecs: false,
      col: "",
      dateFormat: "yyyy-MM-dd HH:mm:ss"
    }, {
      headers: { "Authorization": `Bearer ${token}` },
      responseType: "blob"
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `metrics_${props.deviceId}_${props.startDate.getTime()}_${props.endDate.getTime()}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  }, [props.deviceId, props.startDate, props.endDate, props.limit]);

  const palettes = {
    aaaa: ['#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd', '#8c564b', '#e377c2', '#7f7f7f', '#bcbd22', '#17becf'],
  };

  const pickByIndex = (array: Array<string>, idx: number) => array[idx % array.length];

  const builder = new UPlotOptsBuilder();
  if (props.metric.columnNames) {
    for (let i = 0; i < props.metric.columnNames.length; i++) {
      builder.addSeries({
        show: true,
        spanGaps: false,
        label: props.metric.columnNames[i],
        value: (_self, rawValue) => rawValue ? rawValue : "--",
        stroke: pickByIndex(palettes["aaaa"], i),
        width: 1
      });
    }
  }
  builder.opts = {
    scales: {
      x: {
        time: true,
      },
    },
    series: builder.series,
    hooks: builder.hooks,
  };

  const data: AlignedData = [
    props.metric.timestamps,
    ...(props.metric.values || []),
  ];

  const height = props.height || 300;

  return (
    <div ref={containerRef} style={{ width: '100%', minHeight: height }}>
      <UPlotReact builder={builder} data={data} width={containerWidth} height={height} key={builder.key} />
    </div>
  );
}

export default MetricChart;
